<template>
  <div
    class="surprise-stock-redeem-wrapper padding-l-vertical l:padding-2xl-vertical">
    <div class="flex-center flex-middle flex-column min-height-page">
      <div
        class="grand-wrapper flex-column bg-alt c-bg padding-l box-shadow-5 relative border-radius">
        <div class="confetti-source relative">
          <confetti-explosion
            class=""
            :duration="confettiDuration"
            :particleSize="10"
            :particleCount="600"
            v-if="isShowingConfetti" />
        </div>

        <div
          class="title-wrapper margin-xl-top flex-middle flex-center margin-auto-inline">
          <div class="fancy-title">
            <h1 class="margin-remove">{{ tu("lp.surpriseStock.ui.title") }}</h1>
            <h2 class="margin-remove text-2xl text-center margin-l-top">
              {{ tu("lp.surpriseStock.ui.explain") }}
            </h2>
          </div>
        </div>

        <div class="scratch-ui-wrapper" v-show="joiner">
          <div class="scratch-area-wrapper margin-inline-auto">
            <div
              class="scratch-area padding-xl relative flex-middle flex-center">
              <div
                class="text-container relative c-alt z-level-2 c-alt text-center">
                <span
                  :class="{ active: didRedeem }"
                  class="fadein-content block margin-remove line-height-090 text-center text-l"
                  >{{ tu("lp.surpriseStock.ui.winTitle") }}</span
                >
                <span
                  class="block text-center text-max letter-spacing-110 line-height-080 bold margin-remove c-alt">
                  {{ joiner?.SurpriseStock?.name }}
                </span>
                <p
                  :class="{ active: didRedeem }"
                  class="fadein-content block line-height-080 margin-m-top text-m text-center">
                  <span class="line-height-090 block">{{
                    tu("lp.surpriseStock.ui.winExplain1")
                  }}</span>
                  <span
                    class="line-height-090 block margin-m-top"
                    style="top: -6px; position: relative"
                    >{{ tu("lp.surpriseStock.ui.winExplain2") }}</span
                  >
                </p>
              </div>
              <div
                class="scratch-overlay scratch-overlay-empty absolute-cover z-level-1"
                :style="`background-image: url('${getAsset(
                  'images/surprise-stock/scratch-card-empty-bg.svg'
                )}');`"></div>
              <div
                v-show="!didRedeem"
                class="scratch-overlay scratch-overlay--action absolute-cover z-level-3"
                :class="{ 'faded-out': triggeredRedeem }"
                ref="scratchOverlayAction">
                <canvas
                  class="absolute-cover scratch-canvas"
                  ref="scratch-canvas"></canvas>
              </div>
            </div>
          </div>

          <div
            class="scratch-indicator flex-middle flex-center margin-xl-bottom appearing-content"
            :class="{ active: !didRedeem }">
            <img :src="getAsset('images/surprise-stock/finger-icon.svg')" />
          </div>
          <div
            v-show="didRedeem"
            class="share flex-middle flex-center flex-column margin-xl-bottom animate__fadeIn animate--fast"
            :class="{ active: didRedeem }">
            <p
              class="text-xl text-center line-height-090 margin-remove"
              v-html="
                textToSafeHtml(
                  t('lp.surpriseStock.ui.whatsappShareCtaExplain1')
                )
              "></p>
            <p
              class="text-xl bold text-center margin-s-top margin-remove-bottom line-height-090"
              v-html="
                textToSafeHtml(
                  t('lp.surpriseStock.ui.whatsappShareCtaExplain2')
                )
              "></p>
            <a
              :href="whatsappShareUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="plain-link block margin-xl-top">
              <form-button
                class="plain-link text-decoration-none"
                icon="meitav-whatsapp"
                theme="lead"
                text="lp.surpriseStock.ui.whatsappShareCta"></form-button>
            </a>
          </div>
        </div>

        <div class="footer-logo flex-middle flex-center margin-xl-bottom">
          <img
            :src="getAsset('images/surprise-stock/meitav-trade-logo.webp')" />
        </div>
      </div>

      <div class="legal margin-auto-inline margin-xl-top">
        <p class="margin-remove text-xs text-inverse">
          {{ tu("lp.surpriseStock.ui.disclaimer") }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import asyncOperations from "@/client/extensions/composition/asyncOperations";
import { computed } from "vue";
import ConfettiExplosion from "vue-confetti-explosion";

export default {
  props: {
    joinerUuid: {
      type: String,
      default: "",
    },
  },
  components: { ConfettiExplosion },
  setup(props) {
    let { asyncOps, asyncOpsReady } = asyncOperations(props);

    return {
      asyncOps,
      asyncOpsReady,
    };
  },
  meta: "lp.surpriseStock.ui.metaTitle",
  data() {
    return {
      joiner: false,
      triggeredRedeem: false,
      isShowingConfetti: false,
      confettiDuration: 5000,
      //todo: remember to give public permission to redeem or use public use case endpoint
      asyncData: {
        joiner: {
          target: `entity/surpriseStockJoiner/${this.joinerUuid}`,
          data: {
            with: "surpriseStock",
            identifier: computed(() => this.joinerUuid),
            lookupField: "uuid",
          },
          options: {
            dataParser: (input) => {
              console.log(input);
              return input.item;
            },
            errorHandler: 404,
          },
        },
      },
    };
  },
  mounted() {
    this.initScratchCanvas();
  },
  methods: {
    async initScratchCanvas() {
      let canvas = this.$refs["scratch-canvas"];
      if (Array.isArray(canvas)) {
        canvas = canvas[0];
      }

      if (!canvas) {
        return;
      }
      const ctx = canvas.getContext("2d");
      const topImage = new Image();
      topImage.src = this.getAsset(
        "images/surprise-stock/scratch-card-full.svg"
      );

      topImage.onload = () => {
        ctx.drawImage(topImage, 0, 0, canvas.width, canvas.height);
      };

      let isDrawing = false;

      // Helper to get the correct x, y in canvas coordinates
      const getPosition = (e, canvas) => {
        const rect = canvas.getBoundingClientRect();

        let clientX, clientY;
        if (e.touches && e.touches.length > 0) {
          // Touch event
          clientX = e.touches[0].clientX;
          clientY = e.touches[0].clientY;
        } else {
          // Mouse event
          clientX = e.clientX;
          clientY = e.clientY;
        }

        // Account for CSS scaling if canvas is visually resized
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        const x = (clientX - rect.left) * scaleX;
        const y = (clientY - rect.top) * scaleY;

        return { x, y };
      };

      const startScratch = (x, y) => {
        isDrawing = true;
        eraseCircle(x, y);
      };

      const moveScratch = (x, y) => {
        if (!isDrawing) return;
        eraseCircle(x, y);
      };

      const endScratch = () => {
        isDrawing = false;
      };

      // This function erases a circular area on the canvas
      const eraseCircle = (x, y) => {
        ctx.globalCompositeOperation = "destination-out";
        ctx.beginPath();
        // Adjust radius as desired
        ctx.arc(x, y, 20, 0, Math.PI * 2, true);
        ctx.fill();
        checkScratchCompletion();
      };

      // Mouse events
      canvas.addEventListener("mousedown", (e) => {
        const { x, y } = getPosition(e, canvas);
        startScratch(x, y);
      });
      canvas.addEventListener("mousemove", (e) => {
        const { x, y } = getPosition(e, canvas);
        moveScratch(x, y);
      });
      canvas.addEventListener("mouseup", endScratch);
      canvas.addEventListener("mouseleave", endScratch);

      // Touch events
      canvas.addEventListener("touchstart", (e) => {
        const { x, y } = getPosition(e, canvas);
        startScratch(x, y);
      });
      canvas.addEventListener("touchmove", (e) => {
        e.preventDefault(); // Prevent page scroll
        const { x, y } = getPosition(e, canvas);
        moveScratch(x, y);
      });
      canvas.addEventListener("touchend", endScratch);

      const checkScratchCompletion = () => {
        const pixelData = ctx.getImageData(
          0,
          0,
          canvas.width,
          canvas.height
        ).data;
        let total = pixelData.length / 4; // total number of pixels
        let count = 0;
        for (let i = 3; i < pixelData.length; i += 4) {
          // alpha channel is every 4th item in the array, starting at index 3
          if (pixelData[i] === 0) {
            count++;
          }
        }
        const erasedPercentage = (count / total) * 100;
        if (erasedPercentage > 50) {
          this.redeemStock();
        }
      };
    },

    async reportRedeemServerSide() {
      let url = `surprise-stock/joiner/redeem/${this.joinerUuid}`;
      let data = {};
      let redeemResult;
      try {
        redeemResult = await this.asyncOps.patch(url, data);
      } catch (e) {
        return false;
      }

      if (redeemResult.isError) {
        return false;
      }
      console.log("return true");
      return true;
    },
    async redeemStock() {
      if (this.triggeredRedeem) {
        return false;
      }

      this.triggeredRedeem = true;
      this.isShowingConfetti = true;

      let reportResult = await this.reportRedeemServerSide();

      // try to report a few times if there was a problem
      (async () => {
        if (reportResult) {
          return true;
        }

        let attempts = 10;
        let delayInterval = 1000;
        let delay = 1000;

        for (let i = 0; i < attempts; i++) {
          // safety
          if (i > 20) {
            break;
          }
          reportResult = await this.reportRedeemServerSide();
          if (reportResult === true) {
            this.refreshAsyncData();
            break;
          } else {
            await utilities.wait(delay);
            delay = delay + delayInterval;
          }
        }
      })();

      await utilities.wait(this.confettiDuration - 3500);
      this.joiner.didRedeem = true;

      this.refreshAsyncData();
    },
  },
  computed: {
    didRedeem() {
      if (!this.joiner) {
        return false;
      }
      return this.joiner.didRedeem;
    },
    whatsappShareUrl() {
      const text = this.t("lp.surpriseStock.ui.whatsappShareText");
      const link = this.t("lp.surpriseStock.ui.whatsappShareLink");
      const fullText = `${text}\n${link}`;
      const encoded = encodeURIComponent(fullText);

      return `https://api.whatsapp.com/send?text=${encodeURIComponent(
        fullText
      )}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.surprise-stock-redeem-wrapper {
  --ss-container-width: calc(var(--base-margin) * 360);

  .grand-wrapper {
    // temp - mobile only
    max-width: var(--ss-container-width);
    max-height: 780px;
    min-width: var(--ss-container-width);
    min-height: 700px;
    overflow: hidden;

    .confetti-source {
      position: absolute;
      top: 50%;
      inset-inline-start: 50%;
      transform: translateY(-50%), translateX(-50%);
      height: 0;
      width: 0;
      pointer-events: none;
      z-index: var(--z-top-1);
    }

    .fancy-title {
      width: 100%;
      height: auto;

      h1 {
        transform: rotate(7deg);
        text-shadow: 0 0 16px rgba(0, 0, 0, 0.15);
        -webkit-text-stroke: 2px var(--c-gray-1);
        font-size: 80px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        line-height: 0.64;
        //letter-spacing: -2.49px;
        text-align: center;
        color: var(--c-lead-2);
      }
    }

    .scratch-area {
      margin-top: var(--margin-xl);
      margin-bottom: var(--margin-m);
      min-height: 230px;
      max-width: 340px;
      touch-action: none; /* modern Edge/Chrome/Firefox */
      -ms-touch-action: none; /* old IE/Edge */

      .scratch-overlay {
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center center;
        height: 100%;
        width: 100%;
        opacity: 1;
        will-change: opacity;

        transition: opacity 900ms ease-in;

        &.faded-out {
          opacity: 0;
        }
      }

      .text-container {
      }
    }

    .scratch-indicator {
      img {
        object-fit: contain;
        max-width: 100%;
        max-height: 100%;
        height: 40px;
        width: auto;
      }
    }

    .redeem-wrapper {
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center center;
      height: 100%;
      width: 100%;
      min-height: 340px;
      max-width: 340px;
      width: 100%;
    }

    .appearing-content {
      transition: opacity 150ms ease;
      opacity: 0;
      height: 0 !important;
      pointer-events: none;

      &:not(.active) {
        opacity: 1;
        height: auto;
        pointer-events: auto;
        margin: 0 !important;
        padding: 0 !important;
      }

      &.active {
        opacity: 1;
        height: auto;
        pointer-events: auto;
      }
    }

    .fadein-content {
      transition: opacity 150ms ease;
      opacity: 0;

      &.active {
        opacity: 1;
      }
    }

    .footer-logo {
      margin-top: auto;
      height: 60px;
      flex: 0 0 60px;

      img {
        object-fit: contain;
        max-height: 100%;
        max-width: 100%;
      }
    }
  }

  .legal {
    width: var(--ss-container-width);
    min-width: var(--ss-container-width);
    max-width: var(--ss-container-width);

    p {
      text-align: justify;
    }
  }
}
</style>
